/**
 * How a player bats and bowls, as words (ADR-0023).
 *
 * Pure, like everything in this package: the scorer, the server-rendered pages and the React
 * Native app all print these, so a left-arm finger spinner is "Slow left-arm orthodox" on every
 * screen and can only be misnamed in one place.
 *
 * The bowling *style* a cricketer would say is made from the arm and the type together and is
 * never stored. What is stored is the arm and the type, separately, so "against spin" or
 * "against left-arm" can be asked later without parsing a label.
 */
import type { BatterHand, BowlingArm, BowlingType } from "./events.ts";

/** "Left-handed" / "Right-handed", for a sentence. */
export const battingHandName = (hand: BatterHand): string => (hand === "left" ? "Left-handed" : "Right-handed");

/** "LHB" / "RHB", for a tile with no room. */
export const battingHandShort = (hand: BatterHand): string => (hand === "left" ? "LHB" : "RHB");

/** Seam is fast or medium; spin is finger or wrist spin. */
export const bowlingFamily = (type: BowlingType): "seam" | "spin" =>
  type === "fast" || type === "medium" ? "seam" : "spin";

/** The type on its own, for a button. */
export const bowlingTypeName = (type: BowlingType): string => {
  switch (type) {
    case "fast":
      return "Fast";
    case "medium":
      return "Medium";
    case "finger_spin":
      return "Finger spin";
    case "wrist_spin":
      return "Wrist spin";
  }
};

/**
 * The name a cricketer would say. Null when nothing is known, so a caller can print "Not recorded
 * yet" instead of guessing.
 */
export function bowlingStyleName(arm: BowlingArm | null, type: BowlingType | null): string | null {
  if (arm === null && type === null) return null;
  if (type === null) return arm === "left" ? "Left-arm bowler" : "Right-arm bowler";
  if (arm === null) return bowlingTypeName(type);
  const side = arm === "left" ? "Left" : "Right";
  switch (type) {
    case "fast":
      return `${side}-arm fast`;
    case "medium":
      return `${side}-arm medium`;
    case "finger_spin":
      return arm === "left" ? "Slow left-arm orthodox" : "Right-arm off-break";
    case "wrist_spin":
      return arm === "left" ? "Left-arm wrist spin" : "Right-arm leg-break";
  }
}

/** The scorecard abbreviation: RF, LM, OB, SLA, LB, LWS. Null when nothing is known. */
export function bowlingStyleShort(arm: BowlingArm | null, type: BowlingType | null): string | null {
  if (arm === null && type === null) return null;
  if (type === null) return arm === "left" ? "L-arm" : "R-arm";
  if (arm === null) return bowlingTypeName(type);
  switch (type) {
    case "fast":
      return arm === "left" ? "LF" : "RF";
    case "medium":
      return arm === "left" ? "LM" : "RM";
    case "finger_spin":
      return arm === "left" ? "SLA" : "OB";
    case "wrist_spin":
      return arm === "left" ? "LWS" : "LB";
  }
}
